import React from 'react';
import { Phone, Mail, MapPin, Menu, X, ShoppingCart, User, Heart, GitCompare, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const { getCartCount, setIsCartOpen } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu when clicking outside
  React.useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navigateTo = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const cartCount = getCartCount();

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <a href="tel:+919887068888" className="flex items-center gap-2 hover:text-green-400 transition-colors">
                <Phone size={14} />
                <span className="hidden sm:inline">+91 9887068888</span>
              </a>
              <a href="mailto:support@rodekho.com" className="flex items-center gap-2 hover:text-green-400 transition-colors">
                <Mail size={14} />
                <span className="hidden md:inline">support@rodekho.com</span>
              </a>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <MapPin size={14} />
              <span>Jaipur, Rajasthan</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <button onClick={() => navigateTo('/')} className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="RO Dekho - Purification Ka Expert" 
                className="h-12 sm:h-16 md:h-[72px] w-auto object-contain"
                data-testid="header-logo"
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              <button onClick={() => navigateTo('/')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">
                Home
              </button>
              <button onClick={() => navigateTo('/ro-on-rent')} className="text-gray-700 hover:text-lime-600 font-medium transition-colors">
                RO on Rent
              </button>
              <button onClick={() => navigateTo('/products')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">
                RO Systems
              </button>
              <button onClick={() => navigateTo('/spare-parts')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">
                Spare Parts
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">
                Services
              </button>
              <button onClick={() => navigateTo('/service-areas')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors" data-testid="nav-service-areas">
                Service Areas
              </button>
              <button onClick={() => navigateTo('/team')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">
                About Us
              </button>
              <button onClick={() => navigateTo('/blog')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">
                Blog
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">
                Contact
              </button>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Compare */}
              <button 
                onClick={() => navigateTo('/compare')}
                className="relative p-2 text-gray-700 hover:text-blue-700 transition-colors"
                title="Compare Products"
              >
                <GitCompare size={22} />
              </button>

              {/* Wishlist - Only for logged-in users */}
              {isAuthenticated && (
                <button 
                  onClick={() => navigateTo('/account?tab=wishlist')}
                  className="relative p-2 text-gray-700 hover:text-red-500 transition-colors"
                  title="Wishlist"
                >
                  <Heart size={22} />
                </button>
              )}

              {/* Cart */}
              <button 
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 text-gray-700 hover:text-blue-700 transition-colors"
              >
                <ShoppingCart size={22} />
                {cartCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white text-xs w-5 h-5 flex items-center justify-center p-0 rounded-full">
                    {cartCount}
                  </Badge>
                )}
              </button>

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative user-menu-container">
                  <button 
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <img
                      src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=3B82F6&color=fff&size=32`}
                      alt={user?.name}
                      className="w-8 h-8 rounded-full object-cover border-2 border-blue-100"
                    />
                  </button>
                  
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border py-2 animate-in fade-in slide-in-from-top-2">
                      <div className="px-4 py-3 border-b">
                        <p className="font-semibold text-gray-900 truncate">{user?.name}</p>
                        <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                      </div>
                      <Link 
                        to="/account" 
                        className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <User size={18} />
                        My Account
                      </Link>
                      <Link 
                        to="/account?tab=orders" 
                        className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <ShoppingCart size={18} />
                        My Orders
                      </Link>
                      <Link 
                        to="/account?tab=wishlist" 
                        className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Heart size={18} />
                        Wishlist
                      </Link>
                      <div className="border-t my-1"></div>
                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50"
                      >
                        <LogIn size={18} className="rotate-180" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Button 
                  onClick={() => navigateTo('/login')}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <User size={18} />
                  Login
                </Button>
              )}

              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 text-white px-5 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
              >
                Book Service
              </Button>
            </div>

            {/* Mobile Menu Button & Cart */}
            <div className="lg:hidden flex items-center gap-2">
              <button 
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 text-gray-700 hover:text-blue-700 transition-colors"
              >
                <ShoppingCart size={22} />
                {cartCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white text-xs w-5 h-5 flex items-center justify-center p-0 rounded-full">
                    {cartCount}
                  </Badge>
                )}
              </button>
              
              {isAuthenticated ? (
                <button 
                  onClick={() => navigateTo('/account')}
                  className="p-1"
                >
                  <img
                    src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=3B82F6&color=fff&size=28`}
                    alt={user?.name}
                    className="w-7 h-7 rounded-full object-cover"
                  />
                </button>
              ) : (
                <button 
                  onClick={() => navigateTo('/login')}
                  className="p-2 text-gray-700 hover:text-blue-700"
                >
                  <User size={22} />
                </button>
              )}
              
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-700 hover:text-blue-700 transition-colors p-1"
              >
                {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200 animate-in slide-in-from-top-5">
              <nav className="flex flex-col gap-3">
                <button onClick={() => navigateTo('/')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  Home
                </button>
                <button onClick={() => navigateTo('/ro-on-rent')} className="text-gray-700 hover:text-lime-600 font-medium text-left py-2 transition-colors">
                  RO on Rent
                </button>
                <button onClick={() => navigateTo('/products')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  RO Systems
                </button>
                <button onClick={() => navigateTo('/spare-parts')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  Spare Parts
                </button>
                <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  Services
                </button>
                <button onClick={() => navigateTo('/service-areas')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors" data-testid="mobile-nav-service-areas">
                  Service Areas
                </button>
                <button onClick={() => navigateTo('/team')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  About Us
                </button>
                <button onClick={() => navigateTo('/blog')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  Blog
                </button>
                <button onClick={() => navigateTo('/compare')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  Compare Products
                </button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  Contact
                </button>
                <button onClick={() => navigateTo('/track/order')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                  Track Order
                </button>
                
                {isAuthenticated ? (
                  <>
                    <button onClick={() => navigateTo('/account')} className="text-gray-700 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                      My Account
                    </button>
                    <button onClick={handleLogout} className="text-red-600 hover:text-red-700 font-medium text-left py-2 transition-colors">
                      Logout
                    </button>
                  </>
                ) : (
                  <button onClick={() => navigateTo('/login')} className="text-blue-600 hover:text-blue-700 font-medium text-left py-2 transition-colors">
                    Login / Sign Up
                  </button>
                )}
                
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold shadow-md mt-2"
                >
                  Book Service
                </Button>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
